/**
 * Content Image Service
 * Handles uploading images for rich text content (embedded in RichTextEditor)
 */
import apiClient from './apiClient';

const contentImageService = {
  /**
   * Upload an image for rich text content
   * The image is created in pending state and will be linked to parent on save.
   * @param {File} file - Image file to upload
   * @param {string} parentType - Entity type (e.g., "Event", "Survey", "Article")
   * @param {Object} options - Optional request options (e.g., signal for cancellation)
   * @returns {Promise<{id: number, url: string, fileName: string, storedFileName: string, contentType: string, fileSizeBytes: number, width: number|null, height: number|null}>}
   */
  async upload(file, parentType, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('parentType', parentType);

    // apiClient.post already returns response.data (unwrapped)
    const data = await apiClient.post('/content-images/upload', formData, {
      // Don't set Content-Type - axios will set it with the correct boundary for FormData
      ...options,
    });
    return data;
  },

  /**
   * Get orphaned content images (admin only)
   * @param {number} hoursOld - Only return images older than this (default: 24)
   * @param {Object} options - Optional request options
   * @returns {Promise<Array<{id: number, url: string, fileName: string, contentParentType: string, dateCreated: string, uploadedByUserId: string}>>}
   */
  async getOrphaned(hoursOld = 24, options = {}) {
    const data = await apiClient.get('/content-images/orphaned', {
      params: { hoursOld },
      ...options,
    });
    return data;
  },

  /**
   * Trigger cleanup of orphaned content images (admin only)
   * @param {number} hoursOld - Delete images older than this (default: 24)
   * @param {Object} options - Optional request options
   * @returns {Promise<{deletedCount: number, thresholdHours: number}>}
   */
  async cleanup(hoursOld = 24, options = {}) {
    const data = await apiClient.delete('/content-images/cleanup', {
      params: { hoursOld },
      ...options,
    });
    return data;
  },
};

export default contentImageService;
